import { useQuery } from '@tanstack/react-query'
import { useAuthenticatedClient } from '@/api/queries'
import type { components } from '@/api/schema'
import type { StatusTone } from '@/lib/status-tones'

export type ReadinessStep = components['schemas']['ReadinessStepRead']
export type ReadinessStatus = ReadinessStep['status']
export type StoreReadinessProduct = components['schemas']['StoreReadinessProductRead']
export type StoreReadinessManufacturer = components['schemas']['StoreReadinessManufacturerRead']
export type ProductStoreReadiness = components['schemas']['ProductStoreReadinessRead']

export type StoreReadinessParams = {
  manufacturer_uuid?: string
  step?: string
  status?: 'stuck' | 'waiting' | 'in_progress'
  search?: string
  page?: number
  limit?: number
}

/** The steps a product needs before it goes live, in order (``app.services.catalog.store_readiness``). */
export const READINESS_STEPS = [
  { value: 'price', label: 'Price' },
  { value: 'title', label: 'Title' },
  { value: 'documents', label: 'Documents read' },
  { value: 'content', label: 'Content' },
  { value: 'configuration', label: 'Configuration check' },
  { value: 'pictures', label: 'Pictures' },
  { value: 'category', label: 'Store category' },
  { value: 'shipping', label: 'Shipping estimate' },
  { value: 'source', label: 'AutoQuotes data' },
  { value: 'listing', label: 'Shopify listing' },
  { value: 'publishing', label: 'Publishing' },
]

export const READINESS_STATUS: Record<ReadinessStatus, { label: string; tone: StatusTone }> = {
  stuck: { label: 'Stuck', tone: 'danger' },
  waiting: { label: 'Waiting', tone: 'neutral' },
  in_progress: { label: 'In progress', tone: 'progress' },
  done: { label: 'Done', tone: 'success' },
}

export const storeReadinessKeys = {
  all: ['store-readiness'] as const,
  products: (params: StoreReadinessParams) => ['store-readiness', 'products', params] as const,
  manufacturers: ['store-readiness', 'manufacturers'] as const,
  product: (uuid: string) => ['store-readiness', 'product', uuid] as const,
}

export function useStoreReadinessProducts(params: StoreReadinessParams, enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: storeReadinessKeys.products(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/store-readiness/products', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    enabled,
  })
}

export function useStoreReadinessManufacturers() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: storeReadinessKeys.manufacturers,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/store-readiness/manufacturers')
      if (error) throw error
      return data
    },
  })
}

export function useProductStoreReadiness(productUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: storeReadinessKeys.product(productUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/store-readiness/products/{product_uuid}', {
        params: { path: { product_uuid: productUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!productUuid,
  })
}
